import { Player } from "live/modules"
import { VodPlayer } from "vod/contexts"

import { DisplayRatio } from "./cookies"

function init(
  swfName: string,
  options: PlayerOption | VodPlayerOption,
  videoElement: HTMLDivElement | null,
): void {
  window.swfobject.embedSWF(
    `${process.env.PUBLIC_URL}/${swfName}.swf`,
    videoElement,
    "100%",
    "100%",
    window.swfobject.getFlashPlayerVersion().major,
    `${process.env.PUBLIC_URL}/expressInstall.swf`,
    options,
    { wmode: "opaque" },
  )
}

function getObjectById(videoElementId: string): any {
  return swfobject.getObjectById(videoElementId)
}

function createFlashPlayer(
  videoElement: HTMLDivElement,
  { domain, application, streamkey }: PlayerOption,
  handleFullMode: Function,
): Promise<Player> {
  const options = {
    domain,
    application,
    streamkey,
  }

  return new Promise((resolve) => {
    init("wownet", options, videoElement)
    ;(window as any).Complete = () => {
      const flashPlayer = getObjectById(videoElement!.id)

      flashPlayer.start = () => {
        if (!flashPlayer.isReady) return
        flashPlayer.SetPlay("start")
      }

      flashPlayer.stop = () => {
        flashPlayer.SetPlay("stop")
      }

      flashPlayer.setStreamURL = ({
        domain,
        application,
        streamkey,
      }: PlayerOption) => {
        if (!flashPlayer.isReady) return
        flashPlayer.SetStreamInfo(domain, application, streamkey)
      }

      flashPlayer.setVolume = (volmue: number) => {
        flashPlayer.SetVolumeChange(volmue / 100)
      }

      flashPlayer.requestFullscreen = handleFullMode

      flashPlayer.changeDisplayRatio = (displayRatio: DisplayRatio) => {
        flashPlayer.SetFullScreenSize(
          displayRatio === "displayRatio" ? "on" : "off",
        )
      }

      const DELAY = 5000
      setTimeout(() => (flashPlayer.isReady = true), DELAY)

      resolve(flashPlayer as Player)
    }
  })
}

function createFlashVodPlayer(
  videoElement: HTMLDivElement,
  options: VodPlayerOption,
  handleFullMode: Function,
): Promise<VodPlayer> {
  function addEventLitener(flashPlayer: any) {
    const global = window as any

    global.VideoPlaySec = (sec: number) => {
      flashPlayer.playingTime = sec
      flashPlayer.timeupdate()
    }

    global.VideoEnded = () => {
      flashPlayer.ended()
    }

    global.VideoSeeking = () => {
      flashPlayer.seeking()
    }
  }

  return new Promise((resolve) => {
    init("wownet_vod_player", options, videoElement)

    const flashPlayer = getObjectById(videoElement!.id)

    addEventLitener(flashPlayer)

    flashPlayer.setStreamUrl = (flag: string, filename: string) => {
      if (!flashPlayer.isReady) return
      flashPlayer.VideoPlay(flag, filename)
    }

    flashPlayer.on = (eventType: string, callback: Function) => {
      if (eventType === "timeupdate") {
        flashPlayer.timeupdate = callback
      }

      if (eventType === "seeking") {
        flashPlayer.seeking = callback
      }

      if (eventType === "ended") {
        flashPlayer.ended = callback
      }
    }

    flashPlayer.requestFullscreen = handleFullMode

    flashPlayer.off = (eventType: string) => {
      if (eventType === "timeupdate") {
        flashPlayer.playingTime = null
        flashPlayer.timeupdate = null
      }

      if (eventType === "seeking") {
        flashPlayer.seeking = null
      }

      if (eventType === "ended") {
        flashPlayer.ended = null
      }
    }

    flashPlayer.currentTime = () => {
      return flashPlayer.playingTime
    }

    const DELAY = 2000
    setTimeout(() => (flashPlayer.isReady = true), DELAY)

    resolve(flashPlayer as VodPlayer)
  })
}

export { createFlashPlayer, createFlashVodPlayer }

interface PlayerOption {
  domain: string
  application: string
  streamkey: string
}

interface VodPlayerOption {
  domain: string
  vodPath: string
  vodFlag: string
  filename: string
}
